import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import {
  BOARD_DISPLAY_NAME,
  IS_MIX_CATALOG,
  MIX_BOARD_NAMES,
} from "@/config/browserBoard";
import {
  getArchivePageNumbers,
  getArchivePageRedirect,
  normalizeArchivePage,
  normalizeArchiveTotalPages,
} from "@/features/archive/archivePagination";
import { ArchiveSearchBar } from "@/features/archive/components/ArchiveSearchBar";
import { ArchiveStorageDisclosure } from "@/features/archive/components/ArchiveStorageDisclosure";
import { ArchiveStorageGauge } from "@/features/archive/components/ArchiveStorageGauge";
import { DesktopArchiveGrid } from "@/features/archive/components/desktop/DesktopArchiveGrid";
import { DesktopArchiveToolbar } from "@/features/archive/components/desktop/DesktopArchiveToolbar";
import { ArchiveGrid } from "@/features/archive/components/lists/ArchiveGrid";
import {
  useArchiveStorage,
  useArchiveStorages,
} from "@/features/archive/hooks/useArchiveStorage";
import { useArchiveThreads } from "@/features/archive/hooks/useArchiveThreads";
import {
  type ArchiveEvent,
  type ArchiveEventNotifier,
  isArchiveEvent,
} from "@/features/archive/mediators/archiveEvents";
import {
  type ArchivePageEffect,
  type ArchivePageState,
  createArchivePageMediator,
  isArchivePageEvent,
} from "@/features/archive/mediators/archivePageMediator";
import {
  getCatalogPageLimit,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import {
  normalizeSearchScope,
  type SearchScope,
  serializeSearchScope,
} from "@/features/search/searchScope";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import {
  resolveEnabledMixSlugs,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { useIsDesktop } from "@/shared/hooks/useMediaQuery";
import {
  getAddedBoardNames,
  subscribeAddedBoardNames,
} from "@/shared/lib/addedBoardNames";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { LoadingScreen, RefreshStatus } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";
import { Button } from "@/shared/ui/form";

type ArchiveQueryStateProps = {
  readonly children: React.ReactNode;
  readonly error: unknown;
  readonly hasData: boolean;
  readonly isLoading: boolean;
  readonly isRetrying: boolean;
};

type ArchiveQueryStateViewProps = ArchiveQueryStateProps & {
  readonly onEvent: ArchiveEventNotifier;
};

type ArchivePageLayoutProps = {
  readonly children: React.ReactNode;
  readonly desktopHistorySidebarRef: React.RefObject<DesktopHistorySidebarHandle | null>;
  readonly error: unknown;
  readonly hasData: boolean;
  readonly isDesktop: boolean;
  readonly isFetching: boolean;
  readonly isLoading: boolean;
  readonly isManualRefreshing: boolean;
  readonly isRetrying: boolean;
  readonly isSettingsOpen: boolean;
  readonly onEvent: ArchiveEventNotifier;
  readonly searchQuery: string;
  readonly searchScope: SearchScope;
};

type ArchivePaginationProps = {
  readonly currentPage: number;
  readonly totalPages: number;
};

type ArchivePaginationViewProps = ArchivePaginationProps & {
  readonly onEvent: ArchiveEventNotifier;
};

export const ArchiveQueryState: React.FunctionComponent<
  ArchiveQueryStateProps
> = (props: ArchiveQueryStateProps) => {
  const node = useComponentNode();
  const notify: ArchiveEventNotifier = (
    event: ArchiveEvent,
  ): ReturnType<ArchiveEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <ArchiveQueryStateView {...props} onEvent={notify} />
    </ComponentScope>
  );
};

export const ArchiveQueryStateView: React.FunctionComponent<
  ArchiveQueryStateViewProps
> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying,
  onEvent,
}: ArchiveQueryStateViewProps) => (
  <QueryStateBoundary
    error={error}
    hasData={hasData}
    isLoading={isLoading}
    isRetrying={isRetrying}
    message="過去ログの読み込みに失敗しました"
    onRetry={(): void => {
      onEvent({ type: "archive/retry-requested" });
    }}
  >
    {children}
  </QueryStateBoundary>
);

/** 過去ログページをpropsだけから描画するView。 */
export const ArchivePageLayout: React.FunctionComponent<
  ArchivePageLayoutProps
> = ({
  children,
  desktopHistorySidebarRef,
  error,
  hasData,
  isDesktop,
  isFetching,
  isLoading,
  isManualRefreshing,
  isRetrying,
  isSettingsOpen,
  onEvent,
  searchQuery,
  searchScope,
}: ArchivePageLayoutProps) => {
  if (isDesktop) {
    return (
      <section
        aria-labelledby="archive-page-title"
        className="desktop-catalog-page"
      >
        <DesktopArchiveToolbar
          isFetching={isFetching}
          searchQuery={searchQuery}
          searchScope={searchScope}
        />
        <RefreshStatus isRefreshing={isManualRefreshing} />
        <h1 id="archive-page-title" className="desktop-mode-title">
          過去ログ
        </h1>
        <ArchiveQueryState
          error={error}
          hasData={hasData}
          isLoading={isLoading}
          isRetrying={isRetrying}
        >
          {children}
        </ArchiveQueryState>
        <DesktopHistorySidebar
          ref={desktopHistorySidebarRef}
          showClosedHandle={false}
        />
        <DesktopSettingsModal
          isOpen={isSettingsOpen}
          onClose={(): void => {
            onEvent({ type: "archive/settings-close-requested" });
          }}
        />
      </section>
    );
  }

  return (
    <section aria-labelledby="archive-page-title">
      <header className="px-4 pt-4">
        <h1
          id="archive-page-title"
          className="text-xl font-bold text-foreground"
        >
          過去ログ
        </h1>
        <ArchiveSearchBar
          className="mt-3"
          isFetching={isFetching}
          query={searchQuery}
          scope={searchScope}
        />
      </header>
      <ArchiveQueryState
        error={error}
        hasData={hasData}
        isLoading={isLoading}
        isRetrying={isRetrying}
      >
        {children}
      </ArchiveQueryState>
    </section>
  );
};

const ArchivePagination: React.FunctionComponent<ArchivePaginationProps> = (
  props: ArchivePaginationProps,
) => {
  const node = useComponentNode();
  const notify: ArchiveEventNotifier = (
    event: ArchiveEvent,
  ): ReturnType<ArchiveEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <ArchivePaginationView {...props} onEvent={notify} />
    </ComponentScope>
  );
};

export const ArchivePaginationView: React.FunctionComponent<
  ArchivePaginationViewProps
> = ({ currentPage, totalPages, onEvent }: ArchivePaginationViewProps) => (
  <nav
    aria-label="過去ログのページ移動"
    className="flex flex-wrap items-center justify-center gap-2 px-4 py-3"
  >
    <Button
      variant="default"
      disabled={currentPage <= 1}
      onClick={(): void => {
        onEvent({ type: "archive/page-change-requested", page: 1 });
      }}
    >
      先頭
    </Button>
    <Button
      variant="default"
      disabled={currentPage <= 1}
      onClick={(): void => {
        onEvent({
          type: "archive/page-change-requested",
          page: currentPage - 1,
        });
      }}
    >
      前へ
    </Button>
    {getArchivePageNumbers(currentPage, totalPages).map((number) => (
      <Button
        key={number}
        variant={number === currentPage ? "primary" : "default"}
        onClick={(): void => {
          onEvent({ type: "archive/page-change-requested", page: number });
        }}
        aria-current={number === currentPage ? "page" : undefined}
      >
        {number}
      </Button>
    ))}
    <Button
      variant="default"
      disabled={currentPage >= totalPages}
      onClick={(): void => {
        onEvent({
          type: "archive/page-change-requested",
          page: currentPage + 1,
        });
      }}
    >
      次へ
    </Button>
    <Button
      variant="default"
      disabled={currentPage >= totalPages}
      onClick={(): void => {
        onEvent({ type: "archive/page-change-requested", page: totalPages });
      }}
    >
      末尾
    </Button>
  </nav>
);

/** Query/store/routerとPassive Viewを接続する過去ログページPresenter。 */
export const ArchivePage: React.FunctionComponent = () => {
  const {
    page = 1,
    q = "",
    scope: serializedScope,
  } = useSearch({
    from: "/archive",
  });
  const navigate = useNavigate({ from: "/archive" });
  const isDesktop = useIsDesktop();
  const columns = useCatalogStore((state) => state.columns);
  const rows = useCatalogStore((state) => state.rows);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const archivePageLimit = getCatalogPageLimit(columns, rows);
  const archiveSearchQuery = q;
  const archiveSearchScope = normalizeSearchScope(serializedScope);
  const archiveSearchScopeParam = serializeSearchScope(archiveSearchScope);
  const archiveQuery = useArchiveThreads(
    page,
    archivePageLimit,
    archiveSearchQuery,
    archiveSearchScope,
  );
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const mixSlugs = useMixCandidateSlugs();
  const boardNames = useSyncExternalStore(
    subscribeAddedBoardNames,
    getAddedBoardNames,
    getAddedBoardNames,
  );
  const boardRevision = useMixStore((state) => state.boardRevision);
  const selectedMixSlugs = useMemo(
    () =>
      IS_MIX_CATALOG ? resolveEnabledMixSlugs(mixSlugs, mixBoardSettings) : [],
    [mixSlugs, mixBoardSettings],
  );
  const storageQuery = useArchiveStorage(!IS_MIX_CATALOG);
  const mixStorageQueries = useArchiveStorages(
    selectedMixSlugs,
    IS_MIX_CATALOG,
    boardRevision,
  );
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const refreshArchive = useCallback(async (): Promise<void> => {
    if (IS_MIX_CATALOG) {
      await Promise.all([
        archiveQuery.refetch(),
        ...mixStorageQueries.map((query) => query.refetch()),
      ]);
      return;
    }
    await Promise.all([archiveQuery.refetch(), storageQuery.refetch()]);
  }, [archiveQuery.refetch, mixStorageQueries, storageQuery.refetch]);
  const isFetching =
    archiveQuery.isFetching ||
    (IS_MIX_CATALOG
      ? mixStorageQueries.some((query) => query.isFetching)
      : storageQuery.isFetching);
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    refreshArchive,
    isFetching,
  );
  const effectRunner = useRef<(effect: ArchivePageEffect) => void>(
    (): void => undefined,
  );
  effectRunner.current = (effect: ArchivePageEffect): void => {
    switch (effect.type) {
      case "navigate-page":
        void navigate({
          search: {
            page: effect.page,
            q: archiveSearchQuery || undefined,
            scope: archiveSearchScopeParam,
          },
        });
        return;
      case "replace-page":
        void navigate({
          search: {
            page: effect.page,
            q: archiveSearchQuery || undefined,
            scope: archiveSearchScopeParam,
          },
          replace: true,
        });
        return;
      case "navigate-search":
        void navigate({
          search: {
            page: 1,
            q: effect.query,
            scope: archiveSearchScopeParam,
          },
        });
        return;
      case "navigate-search-scope":
        void navigate({
          search: {
            page: 1,
            q: archiveSearchQuery || undefined,
            scope: serializeSearchScope(effect.scope),
          },
        });
        return;
      case "refresh":
        if (effect.manual) {
          void refreshManually();
        } else {
          void refreshArchive();
        }
        return;
      case "toggle-sidebar":
        desktopHistorySidebarRef.current?.toggle();
        return;
      case "scroll":
        window.scrollTo({
          top:
            effect.edge === "top" ? 0 : document.documentElement.scrollHeight,
        });
    }
  };
  const mediator = useHierarchyResource((previous) =>
    createArchivePageMediator(
      isDesktop,
      (effect) => effectRunner.current(effect),
      previous?.isDisposed
        ? getHierarchyResourceSnapshot<ArchivePageState>(previous)
        : undefined,
    ),
  );
  const node = useComponentNode((event) => {
    if (!isArchiveEvent(event) || !isArchivePageEvent(event)) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const pageState = useMediatorSnapshot(mediator);
  const notify: ArchiveEventNotifier = (
    event: ArchiveEvent,
  ): ReturnType<ArchiveEventNotifier> => node.dispatch(event).status;
  useDocumentTitle(`過去ログ - ${BOARD_DISPLAY_NAME}`);

  useEffect(() => {
    mediator.dispatch({
      type: "archive/viewport-synchronized",
      isDesktop,
    });
  }, [isDesktop, mediator]);

  const totalPages = normalizeArchiveTotalPages(
    archiveQuery.data?.pagination.totalPages ?? 1,
  );
  const currentPage = normalizeArchivePage(page, totalPages);
  const redirectPage = archiveQuery.data
    ? getArchivePageRedirect(page, totalPages)
    : null;

  useEffect(() => {
    if (redirectPage === null) return;
    const event: ArchiveEvent = {
      type: "archive/page-redirect-requested",
      page: redirectPage,
    };
    node.dispatch(event);
  }, [node, redirectPage]);

  const pagination = totalPages > 1 && (
    <ArchivePagination currentPage={currentPage} totalPages={totalPages} />
  );
  const data = archiveQuery.data;
  const archiveContent = data ? (
    redirectPage !== null ? (
      <LoadingScreen />
    ) : (
      <>
        {IS_MIX_CATALOG
          ? selectedMixSlugs.length > 0 && (
              <ArchiveStorageDisclosure
                isExpanded={pageState.isStorageDisclosureExpanded}
              >
                {mixStorageQueries.map((query, index) => {
                  const slug = selectedMixSlugs[index];
                  return slug ? (
                    <ArchiveStorageGauge
                      key={slug}
                      boardName={
                        MIX_BOARD_NAMES[slug] ?? boardNames[slug] ?? slug
                      }
                      hasError={query.isError}
                      isCompact
                      storage={query.data}
                    />
                  ) : null;
                })}
              </ArchiveStorageDisclosure>
            )
          : (storageQuery.data ?? data.storage) && (
              <ArchiveStorageGauge
                storage={storageQuery.data ?? data.storage}
              />
            )}
        {archiveSearchQuery && (
          <p
            className="px-4 py-2 text-sm text-muted-foreground"
            aria-live="polite"
          >
            「{archiveSearchQuery}」の
            {archiveSearchScope === "op" ? "スレ文" : "全文"}
            の検索結果: {data.pagination.total}件
          </p>
        )}
        {pagination}
        {isDesktop ? (
          <DesktopArchiveGrid
            threads={data.threads}
            columns={columns}
            imageSize={imageSize}
            emptyMessage={
              archiveSearchQuery ? "一致する過去ログはありません" : undefined
            }
          />
        ) : (
          <ArchiveGrid
            threads={data.threads}
            columns={columns}
            emptyMessage={
              archiveSearchQuery ? "一致する過去ログはありません" : undefined
            }
          />
        )}
        {pagination}
      </>
    )
  ) : null;

  return (
    <ComponentScope node={node}>
      <ArchivePageLayout
        desktopHistorySidebarRef={desktopHistorySidebarRef}
        error={archiveQuery.error}
        hasData={data !== undefined}
        isDesktop={isDesktop}
        isFetching={isFetching}
        isLoading={archiveQuery.isLoading}
        isManualRefreshing={isManualRefreshing}
        isRetrying={isFetching}
        isSettingsOpen={pageState.isSettingsOpen}
        onEvent={notify}
        searchQuery={archiveSearchQuery}
        searchScope={archiveSearchScope}
      >
        {archiveContent}
      </ArchivePageLayout>
    </ComponentScope>
  );
};
